import { useState, useEffect, createContext, useContext } from "react";

const CartContext = createContext();

const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const CartProvider = ({ children }) => {
  // Initialize state from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cornerstone_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [bookCartItems, setBookCartItems] = useState(() => {
    try {
      const saved = localStorage.getItem('cornerstone_book_cart');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [bundleItem, setBundleItem] = useState(() => {
    try {
      const saved = localStorage.getItem('cornerstone_bundle');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cornerstone_cart', JSON.stringify(cartItems));
  }, [cartItems]);

  useEffect(() => {
    localStorage.setItem('cornerstone_book_cart', JSON.stringify(bookCartItems));
  }, [bookCartItems]);

  useEffect(() => {
    localStorage.setItem('cornerstone_bundle', JSON.stringify(bundleItem));
  }, [bundleItem]);

  // Coloring packs cart functions
  const addToCart = (pack) => {
    // Clear bundle if adding individual packs
    setBundleItem(null);
    setCartItems(prev => {
      const exists = prev.find(item => item.id === pack.id);
      if (exists) {
        return prev; // Don't add duplicates
      }
      return [...prev, { ...pack, quantity: 1 }];
    });
  };

  const removeFromCart = (packId) => {
    setCartItems(prev => prev.filter(item => item.id !== packId));
  };

  const clearCart = () => {
    setCartItems([]);
    setBundleItem(null);
  };

  const isInCart = (packId) => {
    return cartItems.some(item => item.id === packId);
  };

  // Bundle cart functions
  const addBundleToCart = (bundle, selectedPackIds = []) => {
    // Clear individual packs when adding a bundle
    setCartItems([]);
    setBundleItem({ ...bundle, selectedPacks: selectedPackIds });
  };

  const removeBundleFromCart = () => {
    setBundleItem(null);
  };

  const isBundleInCart = (bundleId) => {
    return bundleItem?.id === bundleId;
  };

  // Books cart functions
  const addBookToCart = (book) => {
    setBookCartItems(prev => {
      const exists = prev.find(item => item.id === book.id);
      if (exists) {
        return prev;
      }
      return [...prev, book];
    });
  };

  const removeBookFromCart = (bookId) => {
    setBookCartItems(prev => prev.filter(item => item.id !== bookId));
  };

  const clearBookCart = () => {
    setBookCartItems([]);
  };

  const isBookInCart = (bookId) => {
    return bookCartItems.some(item => item.id === bookId);
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + item.price, 0);
  const cartCount = cartItems.length;
  const bookCartCount = bookCartItems.length;
  const hasBundle = bundleItem !== null;
  const totalCartCount = (hasBundle ? 1 : cartCount) + bookCartCount;

  // Calculate savings if buying 3+ packs (only when not using bundle)
  const getSavings = () => {
    if (hasBundle) return null; // Bundle already has discount
    if (cartCount >= 6) return { type: 'complete', savings: 1.00, bundlePrice: 5.00 };
    if (cartCount >= 3) return { type: 'bundle3', savings: 0.50, bundlePrice: 2.50 };
    return null;
  };

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
      cartTotal,
      cartCount,
      isCartOpen,
      setIsCartOpen,
      getSavings,
      // Bundle cart
      bundleItem,
      addBundleToCart,
      removeBundleFromCart,
      isBundleInCart,
      hasBundle,
      // Book cart
      bookCartItems,
      addBookToCart,
      removeBookFromCart,
      clearBookCart,
      isBookInCart,
      bookCartCount,
      totalCartCount
    }}>
      {children}
    </CartContext.Provider>
  );
};

export { CartContext, CartProvider, useCart };
